import { useState } from "react";
import { useGetMany, useNotify, useRefresh } from "react-admin";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { ResourceName, type Claimant } from "@/types";
import {
  DETECTION_REASON_LABELS,
  DIFFERING_FIELD_LABELS,
  DUPLICATE_PAIR_STATUS_LABELS,
  type DuplicateClaimantPair,
} from "@/types/duplicateClaimantPair";
import { MergeClaimantPairStep } from "./MergeClaimantPairDialog";
import { ClaimantComparisonTable } from "./ClaimantComparisonTable";
// Shared with the list so the two surfaces cannot format money differently.
import { formatAmount, STATUS_COLORS } from "./DuplicateClaimantPairListCells";

/**
 * Side-by-side review dialog for a suspected duplicate pair (#6717).
 *
 * The list row ranks and surfaces; this is where the call gets made. A reviewer
 * deciding "same person or not" needs the identity fields next to each other —
 * DOB, both identifier spaces, relationship, subscriber — because those are
 * exactly what the matcher guards compared and rejected on. Making them hunt
 * across two claimant pages is what leaves a queue unworked.
 *
 * The decision is the focus, so the comparison is ONE TABLE ordered by evidence
 * (#7224, ClaimantComparisonTable): the fields that DIFFER come first and are
 * marked; agreement is background. The merge step reuses the same table.
 *
 * MERGE (#7064) is offered here and only here -- on a CONFIRMED pair, to
 * operators holding claimant-merges:create (every pair-triage role since
 * 000232; ReadOnly never). Everyone else sees the original copy: confirming records
 * the judgement and the confirmed-but-unmerged backlog stays visible. The AC is
 * explicit that the review list is the single entry point -- no second list.
 *
 * The merge is STEP TWO OF THIS DIALOG, not a second modal stacked on it
 * (house rule: dialogs never stack). Confirming an OPEN pair as a capability
 * holder goes straight into it (#7478) -- one decision, one trip. It is still
 * two calls: the engine only previews and executes a 'confirmed' pair, so the
 * confirm commits first. Backing out returns to this comparison with the pair
 * Confirmed on purpose -- the judgement survives a blocked or deferred merge,
 * and Merge… stays one click away.
 */

interface Props {
  pair: DuplicateClaimantPair | null;
  onClose: () => void;
}

export const DuplicateClaimantPairReviewDialog = ({ pair, onClose }: Props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { canEdit } = useResourcePermissions(ResourceName.DUPLICATE_CLAIMANT_PAIRS);
  // The merge capability is a SEPARATE resource (#7064) so it can be revoked per
  // role without touching dismiss/confirm.
  const { canCreate: canMerge } = useResourcePermissions(ResourceName.CLAIMANT_MERGES);

  const [reason, setReason] = useState("");
  // The reason input exists only once "Not a duplicate" is chosen (#7224): it
  // serves one of three actions, and as a permanent field it was the largest
  // thing on a screen whose job is a comparison.
  const [dismissing, setDismissing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  // Lifted from the merge step so Escape/backdrop cannot close the dialog while
  // the irreversible call is in flight.
  const [mergeBusy, setMergeBusy] = useState(false);
  // The pair confirmed in THIS dialog (#7478). The list hands us a snapshot of
  // the row taken at click time, and refresh() does not replace it, so after
  // confirm-then-merge the prop still says 'open'. Without the override, backing
  // out of the merge re-renders Same person on a pair whose confirm SQL
  // (`status = 'open'`) can no longer match. Between pairs the list remounts
  // this dialog (key={reviewing.id}, DuplicateClaimantPairList), which is what
  // resets it; holding the id rather than a boolean only keeps the override
  // from leaking if the dialog is ever rendered without that key.
  const [confirmedPairId, setConfirmedPairId] = useState<string | null>(null);

  const { data: claimants, isPending } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    { ids: pair ? [pair.claimant_a_id, pair.claimant_b_id] : [] },
    { enabled: !!pair }
  );

  if (!pair) return null;

  const status = confirmedPairId === pair.id ? "confirmed" : pair.status;
  const actionable = status === "open" || status === "confirmed";
  const showCapabilityNote = canEdit && actionable && !canMerge && !dismissing;

  const a = claimants?.find((c) => String(c.id) === pair.claimant_a_id);
  const b = claimants?.find((c) => String(c.id) === pair.claimant_b_id);
  const name = a?.claimant_name || b?.claimant_name;

  // RECORDED EVIDENCE vs the live comparison. differing_fields is what the pair
  // was FLAGGED on at detection time, and #4623's refresh preserves it verbatim
  // rather than recomputing. The grid shows CURRENT disagreement. On a re-raised
  // pair those legitimately diverge: flagged on DOB, and the DOBs now agree --
  // and that divergence is the one thing worth saying out loud (review !4630
  // thread 27543). When nothing flagged has since resolved, the marked rows
  // already carry the evidence and a "Flagged on" line would only repeat the
  // headline (#7224).
  const resolvedSinceFlagging = (pair.differing_fields ?? [])
    .filter((f) => {
      const av = a?.[f as keyof Claimant];
      const bv = b?.[f as keyof Claimant];
      // Only claim resolution when BOTH sides are present and equal. A missing
      // value is not agreement.
      return !!av && !!bv && String(av) === String(bv);
    })
    .map((f) => DIFFERING_FIELD_LABELS[f] ?? f);

  const act = async (action: "dismiss" | "confirm") => {
    setSubmitting(true);
    try {
      await httpClient(`${getPartnerAwareApiUrl()}/duplicate-claimant-pairs/${pair.id}/${action}`, {
        method: "POST",
        body: JSON.stringify(action === "dismiss" ? { reason } : {}),
      });
      if (action === "confirm" && canMerge) {
        // Straight into the merge (#7478). No toast: the merge step opening
        // is the confirmation, and a "you can now merge" toast would point at
        // the step already on screen. Refresh so the row behind reads
        // Confirmed if the operator backs out.
        setConfirmedPairId(pair.id);
        setMergeOpen(true);
        refresh();
        return;
      }
      notify(
        action === "dismiss"
          ? "Marked as not a duplicate"
          : "Confirmed as the same person — merging needs the merge capability, so this stays in the backlog",
        { type: "success" }
      );
      refresh();
      onClose();
      setReason("");
    } catch (err) {
      notify(`Could not ${action} this pair`, { type: "error" });
      console.error(`${action} failed`, err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={mergeBusy ? undefined : onClose} maxWidth="md" fullWidth>
      {mergeOpen && canMerge ? (
        <MergeClaimantPairStep
          pair={pair}
          a={a}
          b={b}
          onBack={() => setMergeOpen(false)}
          onMerged={() => {
            setMergeOpen(false);
            onClose();
          }}
          onBusyChange={setMergeBusy}
        />
      ) : (
        <>
          <DialogTitle variant="h3" sx={{ pb: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <span>Same person?{name ? ` ${name}` : ""}</span>
              {/* A pair that is no longer open says so up front: closed pairs
                  have no actions, and an empty footer needs explaining (!4961). */}
              {status !== "open" && (
                <Box component="span" data-testid="pair-status-chip">
                  <StatusChip
                    label={DUPLICATE_PAIR_STATUS_LABELS[status] ?? status}
                    color={STATUS_COLORS[status] ?? ColorType.Default}
                    size={UIComponentSize.Small}
                  />
                </Box>
              )}
              {/* Same shared StatusChip as the list — the dialog must not invent a
                  second visual language for the same value. */}
              <StatusChip
                label={pair.severity.charAt(0).toUpperCase() + pair.severity.slice(1)}
                color={
                  pair.severity === "high"
                    ? ColorType.Error
                    : pair.severity === "medium"
                      ? ColorType.Warning
                      : ColorType.Default
                }
                size={UIComponentSize.Small}
              />
              {pair.was_re_raised && (
                <StatusChip label="Re-raised" color={ColorType.Info} size={UIComponentSize.Small} />
              )}
            </Stack>
            {/* Why this pair was raised, and what it is worth -- one line. */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {DETECTION_REASON_LABELS[pair.detection_reason] ?? pair.detection_reason}
              {pair.matched_on ? `, matched on "${pair.matched_on}"` : ""} ·{" "}
              <strong>{formatAmount(pair.combined_paid_amount)}</strong> combined
              {pair.specific_deductible ? ` against a ${formatAmount(pair.specific_deductible)} deductible` : ""}
            </Typography>
          </DialogTitle>

          <DialogContent dividers>
            {resolvedSinceFlagging.length > 0 && (
              <Typography variant="body2" sx={{ mb: 1.5 }} data-testid="flagged-on">
                <Box component="span" sx={{ color: "text.secondary" }}>
                  Flagged on:
                </Box>{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {(pair.differing_fields ?? []).map((f) => DIFFERING_FIELD_LABELS[f] ?? f).join(", ")}
                </Box>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {" "}
                  — {resolvedSinceFlagging.join(" and ")}{" "}
                  {resolvedSinceFlagging.length === 1 ? "now matches" : "now match"}
                </Box>
              </Typography>
            )}

            {pair.was_re_raised && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {/* The date tells a reviewer whether this was their own call from last
                    week or someone else's from March, which changes how much they
                    trust it (AC 5, review !4630 thread 27547). */}
                This pair was dismissed
                {pair.dismissed_at ? ` on ${formatLongDate(pair.dismissed_at)}` : " previously"} and returned because
                the underlying evidence changed. Re-check the fields before dismissing again.
              </Alert>
            )}

            {isPending ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <ClaimantComparisonTable pair={pair} a={a} b={b} />
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, justifyContent: showCapabilityNote ? "space-between" : "flex-end" }}>
            {/* Only the operator who CAN confirm but CANNOT merge needs telling
                why there is no Merge here. A capability holder gets the permanence
                statement where it is acted on -- the merge step's acknowledgement
                (#7224). Closed pairs and read-only users have no Confirm for the
                sentence to refer to (!4961). */}
            {showCapabilityNote && (
              <Typography variant="caption" color="text.disabled">
                Confirming records the judgement; merging needs the merge capability.
              </Typography>
            )}

            {dismissing ? (
              /* Reason is REQUIRED. The API will not hold this line -- it discards
                 a malformed body and accepts an empty reason while still stamping
                 dismissed_fingerprint, so an unexplained dismissal would suppress
                 the pair until its evidence changed with nothing recorded about
                 why (review !4630 thread 27546). */
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                <TextField
                  autoFocus
                  size="small"
                  variant="outlined"
                  label="Why these are different people"
                  placeholder="confirmed siblings, different people"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  slotProps={{ htmlInput: { "data-testid": "dismiss-reason" } }}
                  sx={{ flex: 1 }}
                />
                <Button onClick={() => setDismissing(false)} disabled={submitting}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DoNotDisturbOnIcon />}
                  onClick={() => act("dismiss")}
                  disabled={submitting || !reason.trim()}
                  data-testid="dismiss-confirm"
                >
                  Not a duplicate
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button onClick={onClose} disabled={submitting}>
                  Close
                </Button>

                {/* Status gate is ('open','confirmed'), matching
                    DismissClaimantDuplicatePair's own `status IN ('open','confirmed')`.
                    That clause exists for "I confirmed this by mistake". Terminal
                    pairs get no actions at all -- absent, not disabled. */}
                {canEdit && actionable && (
                  <>
                    <Button
                      startIcon={<DoNotDisturbOnIcon />}
                      onClick={() => setDismissing(true)}
                      disabled={submitting}
                      data-testid="dismiss-start"
                    >
                      Not a duplicate
                    </Button>
                    {/* Same person renders ONLY where it can act -- its SQL is
                        `status = 'open'`, and on a confirmed pair its job is done.
                        The ellipsis says a second step follows, same as Merge…
                        (#7478); without the capability it only confirms. */}
                    {status === "open" && (
                      <Button
                        variant="contained"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => act("confirm")}
                        disabled={submitting}
                        data-testid="confirm-same-person"
                      >
                        {canMerge ? "Same person — merge…" : "Same person"}
                      </Button>
                    )}
                  </>
                )}
                {/* On a confirmed pair the merge IS the next action, so it takes the
                    contained/primary slot. Confirmed only: the engine CAS-checks
                    'confirmed' server-side, and only with the capability -- no dead
                    button for everyone else. OUTSIDE the canEdit fragment on purpose:
                    the AC gates the merge on claimant-merges:create ALONE (aem-review F2). */}
                {canMerge && status === "confirmed" && (
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => setMergeOpen(true)}
                    disabled={submitting}
                    data-testid="open-merge-dialog"
                  >
                    Merge…
                  </Button>
                )}
              </Stack>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
